<template lang="html">
  <b-container class="mb-5">
    <b-row>
      <b-col>
        <h2>Suggest a video</h2>
      </b-col>
    </b-row>
    <b-row v-if="videoInfoSubmitted" class="mt-3">
      <b-col>
        <h2>Thank you for your submission!</h2>
        <p>Your link will be reviewed and added to our database.</p>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col md="4">
        <b-form-group
          id="fieldset-1"
          description="We'll check the video on the next step."
          label="Enter the url from YouTube"
          label-for="input-1"
          :invalid-feedback="invalidFeedback"
          :valid-feedback="validFeedback"
          :state="state"
        >
          <b-form-input
            id="input-1"
            v-model="url"
            type="url"
            size="sm"
            :state="state"
            trim
          />
        </b-form-group>

        <b-btn
          :disabled="ukeVideoDataLoading"
          class="mb-4"
          @click="checkForSongInDatabase"
        >
          Find video data<b-spinner
            v-if="ukeVideoDataLoading"
            small
            label="Spinning"
            class="ml-2"
            variant="light"
          />
        </b-btn>

        <div v-if="ukeVideoDataLoaded">
          <b-alert v-if="ukeVideoInDatabase" variant="success" show>
            This video is already in the database. You can submit an update.
          </b-alert>

          <div v-if="videoDataError">
            <div class="alert alert-danger">
              <p>{{ videoDataErrorResponse.message }}</p>
              <p>You may need to mark this item as inaccessible.</p>
              <p>Please check this video manually.</p>
              <b-btn
                :href="`https://www.youtube.com/watch?v=${youtubeId}`"
                target="_blank"
                variant="outline-secondary"
              >
                View online
              </b-btn>
            </div>
          </div>

          <div v-if="ukeVideoInDatabase">
            <b-img thumbnail :src="ukeVideoData.thumbnail.url" />
            <p class="mt-2">
              <b-btn
                :href="`https://www.youtube.com/watch?v=${youtubeId}`"
                target="_blank"
                variant="outline-secondary"
              >
                View online
              </b-btn>
            </p>
          </div>

          <div v-if="!ukeVideoInDatabase && ukeVideoDataLoaded" md="6">
            <b-img
              thumbnail
              :src="youtubeMetadata.snippet.thumbnails['medium'].url"
            />
            <p class="mt-2">
              <b-btn
                :href="`https://www.youtube.com/watch?v=${youtubeId}`"
                target="_blank"
                variant="outline-secondary"
              >
                View online
              </b-btn>
            </p>
          </div>
        </div>
      </b-col>

      <b-col v-if="ukeVideoDataLoaded" md="4">
        <b-form-group
          id="fieldset-title"
          description="Please edit the provided title to include only the song's title"
          label="Edit the title"
          label-for="input-title"
          :invalid-feedback="invalidFeedback"
          :state="titleState"
        >
          <b-form-input
            id="input-1"
            v-model="title"
            type="text"
            :state="titleState"
            trim
          />
        </b-form-group>

        <div class="mb-3">
          <label>Type of video</label>
          <b-form-select v-model="typeSelected" :options="typeOptions" />
        </div>

        <b-form-group
          id="fieldset-email"
          description="If you'd like an email when this video is added, enter an email address here."
          label="Contact email"
          label-for="input-email"
          class="mt-4"
        >
          <b-form-input
            id="input-email"
            v-model="email"
            placeholder="Optional contact email"
            type="email"
          />
        </b-form-group>

        <b-form-textarea
          id="textarea"
          v-model="note"
          placeholder="Add a optional note for the site's editor ..."
          rows="2"
          size="sm"
          max-rows="6"
          class="mt-3"
        />
      </b-col>
      <b-col v-if="ukeVideoDataLoaded">
        <div>
          <div>
            <label>Tuning</label>
            <b-form-select v-model="tuningSelected" :options="tuningOptions" />
          </div>

          <b-form-group
            id="fieldset-key"
            description="Please edit the provided key to include only the song's key"
            label="Song key"
            label-for="input-key"
            class="mt-3"
          >
            <b-form-input id="input-2" v-model="key" type="text" trim />
          </b-form-group>

          <div class="mt-2">
            <label>Song chords</label>
            <vue-tags-input
              v-model="chordTag"
              :tags="chordTags"
              placeholder="Add chord"
              :add-only-from-autocomplete="true"
              :autocomplete-items="filteredChords"
              @tags-changed="(newChords) => (chordTags = newChords)"
            />
          </div>

          <div class="mt-3">
            <ChordDiagram
              v-for="(chord, idx) in chords"
              :key="chord + '_' + idx + '_' + tuningSelected"
              :tuning-override="tuningSelected"
              :chord="chord"
              :small="true"
            />
          </div>

          <!-- Update and submit buttons -->
          <div v-if="ukeVideoInDatabase" class="mt-3">
            <b-btn size="lg" variant="success" @click="songSubmission(false)">
              Update
            </b-btn>
          </div>
          <div v-if="!ukeVideoInDatabase" class="mt-3">
            <b-btn size="lg" variant="success" @click="songSubmission(true)">
              Submit
            </b-btn>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
function getIdFromURL(url) {
  url = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
  // eslint-disable-next-line no-useless-escape
  return url[2] !== undefined ? url[2].split(/[^0-9a-z_\-]/i)[0] : url[0];
}
// import { db } from "@/fstore";
import axios from "axios";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import difference from "lodash/difference";
import isEqual from "lodash/isEqual";
import ChordDiagram from "@/components/ChordDiagram";
import VueTagsInput from "@johmun/vue-tags-input";

export default {
  name: "PublicSubmit",
  components: {
    ChordDiagram,
    VueTagsInput,
  },
  data() {
    return {
      url: "https://www.youtube.com/watch?v=RGkGmJSuvuE",
      //url: "https://www.youtube.com/watch?v=M7ac2OUy6Ss",
      keysToParse: [
        "title",
        "update_date",
        "uploader",
        "uploader_id",
        "uploader_url",
        "thumbnails",
        "duration",
        "description",
        "tags",
        "average_rating",
        "webpage_url",
        "view_count",
      ],
      tuningSelected: "C",
      tuningOptions: [
        { value: "C", text: "C - Standard ukulele" },
        { value: "G", text: "G - Baritone ukulele" },
      ],
      typeSelected: "playalong",
      typeOptions: [
        { value: "playalong", text: "Play-along video" },
        { value: "tutorial", text: "Tutorial video" },
      ],
      ukeVideoData: {},
      fsVideoInDatabase: false,
      ukeVideoDataLoaded: false,
      ukeVideoDataLoading: false,
      ukeVideoInDatabase: false,
      youtubeMetadata: {},
      videoDataLoaded: false,
      videoDataLoading: false,
      videoDataError: false,
      videoDataErrorResponse: {},
      videoInfoSubmitted: false,
      email: "",
      title: "",
      chordTag: "",
      chordTags: [],
      key: "",
      videoData: {},
      note: "",
    };
  },
  computed: {
    state() {
      return this.url.length >= 4 ? true : false;
    },
    titleState() {
      return this.title.length >= 4 ? true : false;
    },
    invalidFeedback() {
      if (this.url.length > 4) {
        return "";
      } else if (this.url.length > 0) {
        return "Enter at least 4 characters";
      } else {
        return "Please enter a YouTube url.";
      }
    },
    validFeedback() {
      return this.state === true ? "Thank you" : "";
    },
    youtubeId() {
      return getIdFromURL(this.url);
    },
    autocompleteChords() {
      return sortBy(
        map(this.chordNames, (c) => {
          return { text: c, length: c.length };
        }),
        ["length", "text"]
      );
    },
    filteredChords() {
      return this.autocompleteChords.filter((i) => {
        return i.text.toLowerCase().startsWith(this.chordTag.toLowerCase());
      });
    },
    chords() {
      return map(this.chordTags, "text");
    },
    chordNames() {
      return map(this.$store.state.chord_list, "name");
    },
    allChordsAvailable() {
      return difference(this.chords, this.chordNames).length == 0;
    },
  },
  watch: {
    chords(value) {
      // Make sure the first letter is capitalized.
      var _chords = map(value, (c) => {
        return c.charAt(0).toUpperCase() + c.slice(1);
      });
      console.log(_chords);
      if (!isEqual(_chords, value)) {
        this.chords = _chords;
      }
    },
    chordTag(value) {
      if (value.length == 0) {
        return;
      }
      if (value.charAt(0).toUpperCase != value.charAt(0)) {
        this.chordTag = value.charAt(0).toUpperCase() + value.slice(1);
      }
    },
  },
  mounted() {
    if (this.$route.params.youtubeId) {
      this.url = `https://www.youtube.com/watch?v=${
        this.$route.params.youtubeId
      }`;
      this.checkForSongInDatabase();
    }
    if (this.$route.query.uri) {
      this.url = this.$route.query.uri;
      this.checkForSongInDatabase();
    }
    if (this.$route.query.tuning) {
      this.tuningSelected = this.$route.query.tuning;
    }
  },
  methods: {
    songSubmission(newSubmission) {
      var _v = this;
      _v.videoInfoSubmitted = true;
      _v.ukeVideoDataLoaded = false;
      this.$store
        .dispatch("addSongSubmission", {
          title: this.title,
          chords: this.chords,
          key: this.key,
          youtube_id: this.youtubeId,
          videoAccessible: this.videoAccessible,
          note: this.note,
          email: this.email,
          primary_tuning: this.tuningSelected,
          content: this.typeSelected,
          newSubmission: newSubmission,
        })
        .then(function() {
          var message;

          if (newSubmission) {
            message = `${_v.title} was successfully added!`;
          } else {
            message = `${_v.title} was successfully updated!`;
          }
          _v.url = "";
          _v.$bvToast.toast(message, {
            title: "Thank you!",
            autoHideDelay: 5000,
            appendToast: true,
            variant: "success",
          });
        });
    },

    checkForSongInDatabase() {
      var _v = this;
      // Check to see if the video is already in the database.

      // http://127.0.0.1:8000/v1/songs/?youtube_id=Iuk1UqrYUqA
      _v.videoInfoSubmitted = false;
      _v.ukeVideoDataLoading = true;
      _v.ukeVideoDataLoaded = false;
      _v.videoDataLoaded = false;
      _v.chordTags = [];
      _v.videoAccessible = true;
      _v.key = "";
      var endpoint = "https://api-py38.ukeability.org/v1/songs";
      axios
        .get(`${endpoint}/?youtube_id=${this.youtubeId}`)
        .then((response) => {
          console.log(response);
          _v.ukeVideoInDatabase = response.data.count > 0;
          if (_v.ukeVideoInDatabase) {
            _v.ukeVideoData = response.data.results[0];
            _v.chordTags = map(_v.ukeVideoData.chord_array, (c) => {
              return { text: c };
            });
            _v.title = _v.ukeVideoData.title;
            _v.key = _v.ukeVideoData.key;
            _v.tuningSelected = _v.ukeVideoData.primary_tuning;
            _v.typeSelected = _v.ukeVideoData.content;
            _v.ukeVideoDataLoaded = true;
            _v.ukeVideoDataLoading = false;
          } else {
            _v.queryProviderForInfo();
          }
        })
        .catch(function(error) {
          console.log("Error getting documents: ", error);
        });
    },
    queryProviderForInfo() {
      var _v = this;
      // Check to see if the video is already in the database.
      var lookup_endpoint = "https://api-py38.ukeability.org/provider/youtube";
      axios
        .get(`${lookup_endpoint}/${_v.youtubeId}`)
        .then(function(response) {
          console.log(response.data);
          if (response.data.status_code == 200) {
            // Live response from server.
            _v.title = response.data.video_data.snippet.title;
            _v.youtubeMetadata = response.data.video_data;
            _v.ukeVideoDataLoaded = true;
            _v.ukeVideoDataLoading = false;
          } else {
            _v.videoDataError = true;
            _v.videoDataErrorResponse = response.data;
            _v.videoAccessible = false;
          }
        })
        .catch(function(error) {
          console.log(error);
          _v.videoDataLoading = true;
        });
    },
  },
};
</script>

<style lang="css" scoped></style>
